// ===========================================
// Safe colour for style sinks
// ===========================================
// A teacher's colour is admin-supplied text. Today both write paths validate it as
// strict hex (`hexColorSchema` in the backend's user.validators.ts), so nothing bad
// can be stored — but MUI's `sx` is a genuine CSS-injection sink, and the safety
// currently lives in the WRITER rather than at the point of use.
//
// MEASURED 2026-09-24 by rendering real payloads through MUI + emotion, because the
// risk is easy to under-rate:
//
//   backgroundColor: 'red; background-image: url(https://evil/x.png)'
//     -> .css-xxx{background-color:red;background-image:url(https://evil/x.png);}
//        (a silent outbound request on every render)
//
//   backgroundColor: 'red } body { display:none } .x {'
//     -> .css-xxx{background-color:red;}
//        body{display:none;}          <-- BROKE OUT of the rule, page-wide
//
// Emotion's stylis parser treats the value as raw CSS text, not an opaque token.
//
// ⚠️ This is the exact shape CLAUDE.md records as a shipped bug: "A sink that is safe
// only because today's writers sanitise WILL break when a second writer is added"
// (2026-09-21, the baseTemplate/tagline incident). The lesson there was to escape at
// the RENDER point, which covers every present and future writer — a CSV teacher
// import or a bulk-edit endpoint that forgets `hexColorSchema` would otherwise make
// this live at every call site at once.

/** Brand primary — the fallback whenever a stored colour is absent or not plain hex. */
export const DEFAULT_ENTITY_COLOR = '#4580E4';

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

/**
 * Return `value` only if it is a plain 6-digit hex colour, otherwise the brand
 * fallback. Use this for ANY DB-sourced colour that reaches `sx`, `style`, or a CSS
 * custom property — never interpolate such a value directly.
 */
export function safeHexColor(value: string | null | undefined): string {
  return value && HEX_COLOR.test(value) ? value : DEFAULT_ENTITY_COLOR;
}
